package features.booking

/**
 * Boleto en HTML, en una sola función para que la descarga y el correo se vean idénticos.
 *
 * ESPEJO de `generarHtmlBoleto` en el repo de Cloud Functions (functions/index.js).
 * El correo es la fuente de verdad: si cambias uno, cambia el otro.
 */

private fun esc(s: String): String = s
    .replace("&", "&amp;")
    .replace("<", "&lt;")
    .replace(">", "&gt;")
    .replace("\"", "&quot;")
    .replace("'", "&#39;")

const val LOGO_SMARTVAN_URL =
    "https://firebasestorage.googleapis.com/v0/b/smartvan-38132.firebasestorage.app/o/public%2Flogotipo_smartvan.png?alt=media"

/** Un boleto (sin <html>). Usar [envolverBoletosParaImprimir] para la ventana de impresión. */
fun generarHtmlBoletoSmartVan(
    nombre: String,
    asiento: String,
    parada: String,
    telefono: String,
    maletasExtras: Int,
    llevaMascota: Boolean,
    ruta: String,
    fecha: String,
    horario: String,
    totalPagado: Int,
    codigoQR: String,
    puntoSubida: String,
    horaSubida: String = "",
    mapsUrl: String = "",
    mapsUrlBajada: String = ""
): String {
    val qrData = esc(codigoQR)
    val abordaje = esc(puntoSubida.ifBlank { "Terminal" })
    val bajada = esc(parada.ifBlank { "---" })
    val horaAbordaje = esc(horaSubida)
    val nombreEsc = esc(nombre)
    val asientoEsc = esc(asiento)
    val rutaEsc = esc(ruta)
    val fechaEsc = esc(fecha)
    val horarioEsc = esc(horario)
    val telefonoEsc = esc(telefono.ifBlank { "N/A" })

    val bloqueHora = if (horaAbordaje.isNotBlank())
        """<br><span style="font-size: 11px; color: #00205B;">🕐 $horaAbordaje</span>""" else ""
    val bloqueMaps = if (mapsUrl.isNotBlank())
        """<br><a href="${esc(mapsUrl)}" style="font-size: 10px; color: #1E88E5; text-decoration: underline;">📍 Ver ubicación en Maps</a>""" else ""
    // Las coords de la bajada ya estaban en catalogos/paradasInfo pero nunca llegaban al
    // pasajero: sabía dónde subir en el mapa, pero no dónde baja.
    val bloqueMapsBajada = if (mapsUrlBajada.isNotBlank())
        """<br><a href="${esc(mapsUrlBajada)}" style="font-size: 10px; color: #EF6C00; text-decoration: underline;">📍 Ver ubicación en Maps</a>""" else ""
    val bloqueMaletas = if (maletasExtras > 0) "<div>• $maletasExtras Maleta(s) Extra</div>" else ""
    val bloqueMascota = if (llevaMascota) "<div>• Viaje con Mascota</div>" else ""

    return """
    <div class="boleto-sv" style="margin-bottom: 30px; max-width: 400px; margin-left: auto; margin-right: auto;">
      <table width="100%" cellpadding="0" cellspacing="0" style="font-family: Helvetica, sans-serif;">
        <tr>
          <td style="background: #00205B; color: white; padding: 15px; text-align: center; border-radius: 10px 10px 0 0;">
            <div style="display: inline-block; background: white; border-radius: 50%; padding: 10px; margin-bottom: 8px;"><img src="$LOGO_SMARTVAN_URL" width="70" style="display: block;" alt="SmartVan" /></div>
            <p style="margin:0; font-size: 12px;">Pase de Abordar - Asiento $asientoEsc</p>
          </td>
        </tr>
        <tr>
          <td style="border: 2px solid #00ACC1; padding: 15px; border-radius: 0 0 10px 10px;">
            <p style="font-size: 18px; font-weight: bold; color: #00ACC1; margin: 10px 0;">$rutaEsc</p>
            <table width="100%" style="margin-bottom: 10px; border-bottom: 1px dotted #ccc; padding-bottom: 10px;">
              <tr>
                <td width="50%">
                  <span style="font-size: 10px; color: #888; text-transform: uppercase;">PASAJERO</span><br>
                  <strong>$nombreEsc</strong>
                </td>
                <td width="50%" style="text-align: right;">
                  <span style="font-size: 10px; color: #888; text-transform: uppercase;">SALIDA</span><br>
                  <strong>$fechaEsc</strong><br>
                  <strong>$horarioEsc</strong>
                </td>
              </tr>
            </table>
            <table width="100%" style="margin-bottom: 10px; border-bottom: 1px dotted #ccc; padding-bottom: 10px;">
              <tr>
                <td width="50%">
                  <span style="font-size: 10px; color: #888; text-transform: uppercase;">ABORDAJE</span><br>
                  <strong style="color:#1E88E5">$abordaje</strong>
                  $bloqueHora
                  $bloqueMaps
                </td>
                <td width="50%" style="text-align: right;">
                  <span style="font-size: 10px; color: #888; text-transform: uppercase;">BAJADA</span><br>
                  <strong style="color:#EF6C00">$bajada</strong>
                  $bloqueMapsBajada
                </td>
              </tr>
            </table>
            <table width="100%" style="margin-bottom: 10px; border-bottom: 1px dotted #ccc; padding-bottom: 10px;">
              <tr>
                <td width="50%" style="text-align: right;">
                  <span style="font-size: 10px; color: #888; text-transform: uppercase;">TELEFONO</span><br>
                  <strong>$telefonoEsc</strong>
                </td>
              </tr>
            </table>
            <div style="background: #f9f9f9; padding: 10px; border-radius: 5px; font-size: 12px;">
              <div style="font-weight:bold; margin-bottom: 5px;">Equipaje y Extras:</div>
              $bloqueMaletas
              $bloqueMascota
              <div style="margin-top:5px; border-top: 1px solid #ddd; padding-top:5px;">
                <strong>Total: $$totalPagado MXN</strong>
              </div>
            </div>
            <div style="background: #F1F8E9; padding: 12px; border-radius: 8px; margin-top: 15px; border: 1px solid #C8E6C9;">
              <div style="font-size: 10px; color: #2E7D32; font-weight: bold; text-transform: uppercase;">Tu Codigo de Viaje</div>
              <div style="font-size: 13px; font-weight: bold; color: #00205B; margin-top: 4px; word-break: break-all;">$qrData</div>
              <div style="font-size: 9px; color: #888; margin-top: 4px;">Usa este código para rastrear tu viaje en vivo en smartvanmx.com o para verificación al abordar</div>
            </div>
            <div style="text-align: center; margin-top: 20px;">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=$qrData" width="150" />
              <p style="font-size:10px; color: #888;">Escanea al abordar</p>
            </div>
            <p style="text-align: center; font-size: 10px; color: #aaa; margin-top: 15px;">
              Gracias por viajar con nosotros. ¡Buen viaje!
            </p>
          </td>
        </tr>
      </table>
    </div>
    """.trimIndent()
}

/** Secciones de Términos y Condiciones. Fuente única: el diálogo de la app y la hoja 2 del PDF. */
fun terminosYCondicionesSecciones(): List<Pair<String, String>> = listOf(
    "1. Aceptación de los Términos" to
        "Al utilizar la plataforma SmartVan para reservar y adquirir boletos de transporte de pasajeros, el usuario acepta en su totalidad los presentes Términos y Condiciones. Si no está de acuerdo con alguno de estos términos, deberá abstenerse de utilizar el servicio.",
    "2. Servicio Ofrecido" to
        "SmartVan es una plataforma digital que permite la reserva y compra de boletos para el servicio de transporte de pasajeros en rutas previamente publicadas. Los viajes son operados por SmartVan o sus operadores asociados.",
    "3. Proceso de Reserva y Compra" to
        "Al seleccionar asientos, el usuario dispone de un tiempo limitado (10 minutos) para completar el registro de pasajeros y realizar el pago. Transcurrido ese tiempo, los asientos serán liberados automáticamente. La reserva se confirma únicamente al completar el pago exitosamente.",
    "4. Precios y Formas de Pago" to
        "Los precios publicados incluyen el boleto base y son expresados en Pesos Mexicanos (MXN). Servicios adicionales como maletas extra y transporte de mascotas tienen un costo adicional. Los precios están sujetos a cambio sin previo aviso. Los métodos de pago aceptados son los indicados en la plataforma al momento de la compra.",
    "5. Política de Cancelación y Reembolso" to
        "Las cancelaciones realizadas por el usuario están sujetas a las políticas vigentes al momento de la compra. SmartVan se reserva el derecho de cancelar viajes por causas de fuerza mayor, en cuyo caso se gestionará el reembolso correspondiente o la reprogramación del viaje. Los reembolsos se procesarán por el mismo método de pago utilizado en la compra.",
    "6. Obligaciones del Pasajero" to
        "El pasajero deberá presentarse en el punto de abordaje con anticipación. Deberá portar una identificación oficial que coincida con el nombre registrado en el boleto. El boleto digital (PDF o QR) deberá ser presentado al momento de abordar. El equipaje permitido es de hasta 10kg por pasajero; el excedente tendrá un cargo adicional.",
    "7. Responsabilidad de SmartVan" to
        "SmartVan se compromete a prestar el servicio de transporte en las condiciones anunciadas. No se responsabiliza por retrasos ocasionados por condiciones climáticas, accidentes viales, bloqueos u otras causas de fuerza mayor.",
    "8. Uso de la Plataforma" to
        "El usuario se compromete a proporcionar información veraz y actualizada. Queda prohibido el uso de la plataforma para fines ilícitos, fraudulentos o que atenten contra los derechos de terceros. SmartVan se reserva el derecho de suspender el acceso a usuarios que incumplan estos términos.",
    "9. Modificaciones" to
        "SmartVan podrá modificar estos Términos y Condiciones en cualquier momento. Las modificaciones entrarán en vigor al ser publicadas en la plataforma. El uso continuado del servicio después de cualquier modificación constituye la aceptación de los nuevos términos.",
    "10. Legislación Aplicable" to
        "Estos Términos y Condiciones se rigen por las leyes de los Estados Unidos Mexicanos. Para cualquier controversia derivada del uso de la plataforma, las partes se someten a la jurisdicción de los tribunales competentes."
)

/**
 * Marca visualmente boletos pagados en sandbox.
 *
 * Un pago de prueba ocupa asiento real y genera un QR válido, así que sin marca es
 * indistinguible de uno vendido. La banda va arriba de cada boleto para que se vea tanto en
 * pantalla como en la captura o la impresión.
 */
fun marcarBoletosComoPrueba(boletosHtml: String): String {
    val banda = """<div style="max-width:400px;margin:0 auto 6px;background:#B71C1C;color:#fff;""" +
        """text-align:center;font-weight:900;font-size:12px;letter-spacing:1px;padding:7px 10px;""" +
        """border-radius:8px;">BOLETO DE PRUEBA · SIN VALOR</div>"""
    return boletosHtml.replace("<div class=\"boleto-sv\"", "$banda<div class=\"boleto-sv\"")
}

/** T&C como página HTML — hoja 2 del PDF descargable. Marca de agua con el logo detrás. */
fun generarHtmlTerminos(): String {
    // Espaciado holgado + fuente algo mayor para que las 10 secciones llenen la hoja y no
    // quede tanto blanco. Dos columnas en pantallas anchas lo comprimirían; una sola es legible.
    val cuerpo = terminosYCondicionesSecciones().joinToString("") { (t, c) ->
        """<div style="margin-bottom:14px;"><div style="font-weight:bold;color:#00205B;font-size:13px;margin-bottom:3px;">${esc(t)}</div><div style="font-size:11px;color:#333;line-height:1.5;">${esc(c)}</div></div>"""
    }
    // box-sizing:border-box + min-height ~ alto útil de A4 (281mm) para que la hoja se sienta
    // llena y la marca de agua quede centrada en la página, no sólo tras el texto.
    // overflow:hidden recorta el logo que sobresale. La marca de agua es un <img> a baja
    // opacidad (print-color-adjust:exact la conserva al imprimir); el texto va en z-index arriba.
    return """
    <div class="terminos-sv" style="position:relative;box-sizing:border-box;max-width:560px;min-height:250mm;margin:0 auto;font-family:Helvetica,sans-serif;padding:26px 22px;background:#fff;overflow:hidden;">
      <img src="$LOGO_SMARTVAN_URL" alt="" aria-hidden="true" style="position:absolute;top:50%;left:50%;width:430px;max-width:80%;transform:translate(-50%,-50%);opacity:0.06;pointer-events:none;z-index:0;" />
      <div style="position:relative;z-index:1;">
        <div style="text-align:center;margin-bottom:20px;padding-bottom:14px;border-bottom:2px solid #00ACC1;">
          <div style="display:inline-block;background:#00205B;border-radius:50%;padding:10px;"><img src="$LOGO_SMARTVAN_URL" width="52" style="display:block;" alt="SmartVan" /></div>
          <div style="font-weight:bold;color:#00205B;font-size:18px;margin-top:8px;">Términos y Condiciones</div>
          <div style="font-size:10px;color:#00ACC1;letter-spacing:1px;text-transform:uppercase;margin-top:2px;">SmartVan · Transporte de pasajeros</div>
        </div>
        $cuerpo
        <div style="font-size:9px;color:#888;margin-top:18px;text-align:center;border-top:1px solid #eee;padding-top:10px;">Última actualización: Junio 2026 · smartvanmx.com</div>
      </div>
    </div>
    """.trimIndent()
}

/**
 * Envuelve los boletos para verlos EN pantalla (no para imprimir): el pasajero los lee y les
 * toma captura. Incluye el aviso de que también llegaron por correo y los botones de acción.
 *
 * Se muestra dentro de la página, así que hereda el ancho del dispositivo: el boleto se centra
 * y el QR queda completo en una captura de celular sin recortarse.
 */
fun envolverBoletosParaPantalla(boletosHtml: String): String = """
<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light">
<title>Tus boletos - SmartVan</title>
<style>
  body { margin:0; background:#f5f7fa; font-family: Helvetica, Arial, sans-serif; }
  .sv-wrap { max-width:460px; margin:0 auto; padding:16px 12px 40px; }
  .sv-btn { flex:1; padding:14px 10px; border-radius:10px; font-size:14px; font-weight:bold; cursor:pointer; }
  .sv-terminos { display:none; }
  @page { size: A4 portrait; margin: 8mm; }
  @media print {
    /* Al imprimir queda solo el boleto + T&C: fuera avisos y botones. */
    body { background:#fff; }
    .sv-no-print { display:none !important; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .boleto-sv { page-break-inside: avoid; break-inside: avoid; page-break-after: always; break-after: page; }
    .boleto-sv:last-of-type { page-break-after: auto; break-after: auto; }
    .boleto-sv img[src*="qrserver"] { width:150px !important; height:auto !important; }
    .sv-terminos { display:block !important; page-break-before: always; }
  }
</style>
</head>
<body>
<div class="sv-wrap">
  <div class="sv-no-print" style="text-align:center; margin-bottom:14px;">
    <div style="font-size:20px; font-weight:900; color:#00205B;">¡Compra exitosa!</div>
    <div style="font-size:13px; color:#555; margin-top:4px;">Estos son tus pases de abordar. Puedes tomarles captura de pantalla.</div>
  </div>
  <div class="sv-no-print" style="background:#E8F5E9; border:1px solid #C8E6C9; border-radius:10px; padding:10px 12px; margin-bottom:16px; text-align:center;">
    <span style="font-size:12px; color:#2E7D32;">📧 También enviamos tus boletos a tu correo electrónico.</span>
  </div>
  $boletosHtml
  <div class="sv-no-print" style="display:flex; gap:10px; margin-top:20px;">
    <button class="sv-btn" style="border:none; background:#00205B; color:#fff;" onclick="window.print()">Descargar / Imprimir</button>
    <button class="sv-btn" style="border:1px solid #00205B; background:#fff; color:#00205B;" onclick="location.href='/'">Volver al inicio</button>
  </div>
  <div class="sv-no-print" style="text-align:center; font-size:11px; color:#888; margin-top:14px;">
    Guarda esta página en favoritos: puedes volver a abrirla para ver tus boletos.
  </div>
  <div class="sv-terminos">${generarHtmlTerminos()}</div>
</div>
</body>
</html>
""".trimIndent()

/**
 * Envuelve uno o más boletos en un documento imprimible.
 *
 * El CSS de impresión vive SÓLO aquí: el boleto se comparte con el correo y no debe cambiar.
 * Sin esto el boleto se partía en dos hojas (el QR caía solo en la página 2).
 */
fun envolverBoletosParaImprimir(boletosHtml: String): String = """
<html>
<head>
<meta charset="utf-8">
<meta name="color-scheme" content="light">
<title>Boletos SmartVan</title>
<style>
  @page { size: A4 portrait; margin: 8mm; }
  body { background: #f5f7fa; padding: 16px; forced-color-adjust: none; }
  /* El boleto es espejo del correo (max-width 400 inline). Sólo en la descarga lo
     agrandamos para que llene mejor la hoja; el correo nunca incluye este wrapper. */
  .boleto-sv { max-width: 500px !important; }
  @media print {
    body { background: #fff; padding: 0; margin: 0; }
    /* Sin esto el navegador descarta los fondos al imprimir: el boleto salía sin la banda
       azul del encabezado ni el recuadro verde del código. */
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    /* Un boleto por hoja, centrado vertical y horizontalmente, entero: nunca cortado. */
    .boleto-sv {
      page-break-inside: avoid;
      break-inside: avoid;
      page-break-after: always;
      break-after: page;
      margin: 0 auto !important;
    }
    .boleto-sv:last-child { page-break-after: auto; break-after: auto; }
    /* QR más grande ahora que la tarjeta llena más ancho (a 500px sí cabe sin desbordar). */
    .boleto-sv img[src*="qrserver"] { width: 150px !important; height: auto !important; }
    .boleto-sv td { padding-top: 0 !important; padding-bottom: 0 !important; }
  }
</style>
</head>
<body>$boletosHtml</body>
</html>
""".trimIndent()
